import React, { useContext, useEffect, useState, Fragment } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { 
    ChevronDownIcon, 
    MagnifyingGlassIcon,
    DocumentTextIcon,
    FunnelIcon,
    XMarkIcon,
    ArrowUturnLeftIcon
} from '@heroicons/react/24/solid';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import axios from '../../axios';
import DoctorCard from './DoctorCard';
import ImportadorModal from './ImportadorModal';
import DoctoresReport from './DoctoresReport';
import DoctoresFiltrosModal from './DoctoresFiltrosModal';
import DatePickerModal from './DatePickerModal';
import { BlobProvider } from '@react-pdf/renderer';
import { UserContext } from '../../UserContext';
import { checkPermiso } from '../../Utils/PermissionsFunctions';
import Loader from '../../Layout/Loaders/Loader';
import NoPermissions from '../NoPermissions/NoPermissions';
import VisitsDateRangeModal from './VisitsDateRangeModal';

const today = new Date().toLocaleDateString("es-MX", { year: 'numeric', month: 'long', day: 'numeric' });

const Doctores = () => {
    const {user} = useContext(UserContext);
    const [doctores, setDoctores] = useState([]);
    const [bulkDoctores, setBulkDoctores] = useState([]);
    const [reportView, setReportView] = useState(false);
    const [instituciones, setInstituciones] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [searchKey, setSearhKey] = useState('');
    const [filtros, setFiltros] = useState({
        especialidad: null,
        institucion: null,
        visitar: null,
        enviados: null,
        representante: null,
        country_id: 42,
        state_id: null,
        municipalty_id: null,
        suburb_id: null,
        start_date: null,
        end_date: null
    });
    const [searching, setSearching] = useState(false);
    const [estados, setEstados] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [suburbs, setSuburbs] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [permisos, setPermisos] = useState({
        doctors_create: null,    
        doctors_read: null,
        doctors_update: null,
        doctors_delete: null,
        doctors_btn_upload: null,
        doctors_btn_report: null
    });

    // Estados para modales (Headless UI)
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [isFiltrosModalOpen, setIsFiltrosModalOpen] = useState(false);
    const [isVisitsModalOpen, setIsVisitsModalOpen] = useState(false);

    useEffect(() => {
        getEstados(42);
        setLoaded(false);
        if(user.id) {
            setPermisos((prevPremisos) => ({
                ...prevPremisos,
                doctors_create: checkPermiso("doctors_create", user.permissions),
                doctors_read: checkPermiso("doctors_read", user.permissions),
                doctors_update: checkPermiso("doctors_update", user.permissions),
                doctors_delete: checkPermiso("doctors_delete", user.permissions),
                doctors_btn_upload: checkPermiso("doctors_btn_upload", user.permissions),
                doctors_btn_report: checkPermiso("doctors_btn_report", user.permissions),
            }));
            _getDoctores();
        }
    }, [user]);

    useEffect(() => {
        (permisos.doctors_read !== null) && setLoaded(true);
    }, [permisos]);

    const _getDoctores = () => {
        axios.get(`api/doctors`).then((res) => {
            setLoading(false);
            console.log("doctores", res.data);
            setDoctores(res.data);
            setBulkDoctores(res.data);
        }).catch(e => console.log("error", e));
    };

    useEffect(() => {
        axios.get('api/institutions').then((resp) => {
            setInstituciones(resp.data);
        }).catch(e => alert(e));
    }, []);

    useEffect(() => {
        axios.get('api/specialties').then((resp) => {
            setEspecialidades(resp.data);
        }).catch(e => console.log("Error al optener especialidades", e));
    }, []);

    const handleKeyPress = (event) => {
        if(event.key === 'Enter') {
            searchKey ? _searchDoctorsField() : setDoctores(bulkDoctores);
        }
    };

    const _searchDoctorsField = () => {
        console.log("Buscando doctor por campo:", searchKey);
        if(searchKey !== '' && searchKey.length >= 1) {
            axios.post('api/searchdoctorfield', {searchword: searchKey}).then(resp => {
                setDoctores(resp.data);
                setSearching(true);
            }).catch((e) => console.log(e));
        }
    };

    const searchByFilters = () => {
        console.log("Filtros", filtros);
        setSearching(false);
        axios.post('api/search_doctor_by_filters', filtros).then(resp => {
            if(resp.status === 200) {
                setDoctores(resp.data);
            }
        }).catch(e => console.log(e));
    };

    const _searchByDate = (fechaInicio, fechaFin) => {
        let fInicio = new Date(fechaInicio);
        let fFin = new Date(fechaFin);

        let tempDoctors = doctores.filter((doc) => {
            let created_at = new Date(doc.created_at);
            if(created_at >= fInicio && created_at <= fFin) {
                return doc;
            } else return null;
        });
        setDoctores(tempDoctors);
    };

    const closeFiltrosModal = () => {
        setIsFiltrosModalOpen(false);
        _limpiarFiltros();
    };

    const _limpiarFiltros = () => {
        setFiltros({
            especialidad: "",
            institucion: "",
            visitar: "",
            enviados: "",
            representante: "",
            country_id: 42,
            state_id: "",
            municipalty_id: "",
            suburb_id: ""
        });
        setMunicipios([]);
        setSuburbs([]);
        setSearhKey('');
        _getDoctores();
    };

    const getEstados = (paisId) => {
        axios.get(`api/states-by-country/${paisId}`)
            .then(res => {
                setEstados(res.data);
            }).catch(e => console.log(e));
    };

    const handleState = (estadoId) => {
        if(estadoId > 0) {
            setFiltros({...filtros, state_id: estadoId});
            axios.get(`api/municipalities-by-state/${estadoId}`)
                .then(res => {
                    setMunicipios(res.data);
                }).catch(e => console.log(e));
        } else {
            setMunicipios([]);
        }
    };

    const handleMunipio = (municipioId) => {
        if(municipioId > 0) {
            setFiltros({...filtros, municipalty_id: municipioId});
            axios.get(`api/suburbs-by-munipality/${municipioId}`)
                .then(res => {
                    setSuburbs(res.data);
                }).catch(e => console.log(e));
        } else {
            setSuburbs([]);
        }
    };

    const _changeReportView = () => {
        setReportView(!reportView);
    };

    const openVisitsModal = () => {
        setIsVisitsModalOpen(true);
    };

    const _handleVisitarSelect = (value) => {
        if(value !== "porregistrar" && value !== "visitados") {
            setFiltros({...filtros, visitar: value, start_date: null, end_date: null});
        } else {
            setFiltros({...filtros, visitar: value});
        }
    };

    const cleanDateFilters = () => {
        setFiltros({...filtros, start_date: null, end_date: null});
    };

    const renderDoctorView = (permisos, loading, doctores) => {
        if (!permisos.doctors_read) {
            return <NoPermissions />;
        }
        return loading ? <Loader /> : <DoctorCard doctores={doctores} permisos={permisos} />;
    };

    const hasActiveFilters = filtros.especialidad || filtros.institucion || filtros.state_id || 
        filtros.municipalty_id || filtros.suburb_id || filtros.visitar || 
        filtros.start_date || filtros.end_date;

    // Contenido de la barra de acciones (Subir, Reporte, Buscar, Filtros)
    // NOTA: Esto es JSX directo, NO un componente funcional, para evitar pérdida de foco en inputs
    const actionBarContent = (
        <div className="flex flex-wrap items-center gap-4">
            {/* Botones de acciones */}
            <div className="flex items-center gap-2">
                {permisos.doctors_btn_upload && (
                    <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors">
                        <img src="/images/subir.png" alt="Subir" className="h-5 w-5" />
                        <span>Subir</span>
                    </button>
                )}
                {permisos.doctors_btn_report && (
                    <button 
                        onClick={_changeReportView}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                    >
                        <img src="/images/pdf-reporte.png" alt="Reporte" className="h-5 w-5" />
                        <span>Reporte</span>
                    </button>
                )}
            </div>

            {/* Barra de búsqueda */}
            <div className="flex-1 min-w-50">
                <div className="relative">
                    <input 
                        id="doctor-search-field"
                        type="text" 
                        placeholder="Filtrar por: ID/Apellido/Nombre/Correo/Compañía"
                        className="dento-input w-full pr-10"
                        onChange={(e) => setSearhKey(e.target.value)}
                        value={searchKey}
                        onKeyDown={handleKeyPress}
                    />
                    <button 
                        onClick={_searchDoctorsField}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-blue-500 hover:text-blue-700 transition-colors"
                    >
                        <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Botones de filtros */}
            <div className="flex items-center gap-2">
                <button 
                    onClick={() => setIsDateModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                >
                    <img src="/images/calendar.png" alt="Fecha" className="h-5 w-5" />
                    <span className="hidden sm:inline">Fecha</span>
                </button>
                <button onClick={() => setIsFiltrosModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                >
                    <FunnelIcon className="h-5 w-5 text-blue-500" />
                    <span className="hidden sm:inline">Filtros</span>
                </button>
                <button 
                    onClick={_limpiarFiltros}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                >
                    <XMarkIcon className="h-5 w-5 text-red-500" />
                    <span className="hidden sm:inline">Limpiar</span>
                </button>
            </div>
        </div>
    );

    return (
    <>
       
            {/* Modales */}
            <DatePickerModal 
                isOpen={isDateModalOpen}
                onClose={() => setIsDateModalOpen(false)}
                onApply={_searchByDate}
                title="Fecha de Alta de doctor"
            />
            
            <DoctoresFiltrosModal
                isOpen={isFiltrosModalOpen}
                onClose={() => setIsFiltrosModalOpen(false)}
                filtros={filtros}
                setFiltros={setFiltros}
                especialidades={especialidades}
                instituciones={instituciones}
                estados={estados}
                municipios={municipios}
                suburbs={suburbs}
                onStateChange={handleState}
                onMunicipioChange={handleMunipio}
                onVisitarChange={_handleVisitarSelect}
                onClear={closeFiltrosModal}
                onApply={searchByFilters}
                openVisitsModal={openVisitsModal}
                cleanDateFilters={cleanDateFilters}
            />
            
            <ImportadorModal />
            <VisitsDateRangeModal 
                setFiltros={setFiltros} 
                isOpen={isVisitsModalOpen}
                onClose={() => setIsVisitsModalOpen(false)}
            />
      
          
        <div className='container-admin-container'>
            {/* Breadcrumb */}
            <div className='container-admin-menu '> 
                <Breadcrumb hasButton={permisos.doctors_create} hasFilters={false} /> 
            </div>

            {/* Contenido principal */}
            <div className='container-admin z-depth-1 p-4'>
                {!reportView ? (
                    loaded ? (
                        <>
                            {/* Barra de acciones móvil */}
                            <div className="md:hidden">
                                <Disclosure>
                                    {({ open }) => (
                                        <>
                                            <DisclosureButton className="flex w-full items-center justify-between rounded-xl bg-blue-50 px-4 py-3 text-left shadow-sm hover:bg-blue-100 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <FunnelIcon className="h-6 w-6 text-blue-600" />
                                                    <span className="font-medium text-gray-800">Filtrar por:</span>
                                                </div>
                                                <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                                            </DisclosureButton>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-200"
                                                enterFrom="opacity-0 -translate-y-1"
                                                enterTo="opacity-100 translate-y-0"
                                                leave="transition ease-in duration-150"
                                                leaveFrom="opacity-100 translate-y-0"
                                                leaveTo="opacity-0 -translate-y-1"
                                            >
                                                <DisclosurePanel className="mt-2 bg-blue-50 rounded-xl p-4 shadow-sm">
                                                    {actionBarContent}
                                                </DisclosurePanel>
                                            </Transition>
                                        </>
                                    )}
                                </Disclosure>
                            </div>
                            {/* Barra de acciones escritorio */}
                            <div className="info-badge rounded-xl p-4 shadow-sm">
                                {actionBarContent}
                            </div>
                            {/* Información de resultados */}
                            <div className="mt-4 px-2">
                                {permisos.doctors_read && (
                                    <div className="text-xl text-gray-700">
                                        <span className="font-bold">Doctores: {doctores.length}</span>
                                        
                                        {searchKey !== '' && searching && (
                                            <span className="ml-2">
                                                | Resultados para: <strong className="text-blue-600">"{searchKey}"</strong>
                                            </span>
                                        )}
                                        
                                        {hasActiveFilters && !searching && (
                                            <span className="ml-2">| Filtros aplicados: </span>
                                        )}
                                        
                                        {hasActiveFilters && !searching && (
                                            <span className="inline-flex flex-wrap gap-2 mt-1">
                                                {filtros.especialidad && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-blue-100 text-blue-700">
                                                        Especialidad: {especialidades.find(e => e.id == filtros.especialidad)?.especialidad}
                                                    </span>
                                                )}
                                                {filtros.institucion && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-blue-100 text-blue-700">
                                                        Institución: {instituciones.find(i => i.id == filtros.institucion)?.institucion}
                                                    </span>
                                                )}
                                                {filtros.state_id && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-green-100 text-green-700">
                                                        Estado: {filtros.state_id}
                                                    </span>
                                                )}
                                                {filtros.municipalty_id && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-green-100 text-green-700">
                                                        Municipio: {filtros.municipalty_id}
                                                    </span>
                                                )}
                                                {filtros.suburb_id && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-green-100 text-green-700">
                                                        Colonia: {filtros.suburb_id}
                                                    </span>
                                                )}
                                                {filtros.visitar && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-purple-100 text-purple-700">
                                                        Visitar: {filtros.visitar}
                                                    </span>
                                                )}
                                                {filtros.enviados && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-orange-100 text-orange-700">
                                                        Enviados: {filtros.enviados}
                                                    </span>
                                                )}
                                                {filtros.start_date && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-cyan-100 text-cyan-700">
                                                        Desde: {filtros.start_date}
                                                    </span>
                                                )}
                                                {filtros.end_date && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-base bg-cyan-100 text-cyan-700">
                                                        Hasta: {filtros.end_date}
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Mensaje sin resultados */}
                            {searching && doctores.length === 0 && (
                                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-amber-700 text-center">
                                        Lo sentimos, no se encontraron resultados. Realiza una nueva búsqueda.
                                    </p>
                                </div>
                            )}

                            {/* Vista de doctores */}
                            <div className="mt-6">
                                {renderDoctorView(permisos, loading, doctores)}
                            </div>
                        </>
                    ) : (
                        <Loader />
                    )
                ) : (
                    /* Vista de Reporte */
                    <>
                        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl mb-6">
                            <div className="flex items-center gap-4">
                                <span className="font-medium text-gray-800">REPORTE - {today}</span>
                                <BlobProvider document={<DoctoresReport doctores={doctores} />}>
                                    {({url}) => (
                                        <a 
                                            href={url} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            <DocumentTextIcon className="h-4 w-4" />
                                            Descargar PDF
                                        </a>
                                    )}
                                </BlobProvider>
                            </div>
                            <button 
                                onClick={_changeReportView}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <ArrowUturnLeftIcon className="h-4 w-4" />
                                Regresar
                            </button>
                        </div>

                        {/* Lista de doctores para reporte */}
                        <div className="space-y-4">
                            {doctores.map((doctor) => (
                                <div key={doctor.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4 border-b border-gray-100 pb-2">
                                            <span className="text-xs text-gray-500">ID: {doctor.id}</span>
                                            <span className="text-xs text-gray-500 ml-4">
                                                Fecha de creación: {new Date(doctor.created_at).toLocaleDateString("es-MX", { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </span>
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Nombre:</label>
                                            <div className="text-sm text-gray-800">{doctor.nombre} {doctor.apellidos}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Compañía:</label>
                                            <div className="text-sm text-gray-800">{doctor.compania}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Teléfono Particular:</label>
                                            <div className="text-sm text-gray-800">{doctor.telefono}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Celular:</label>
                                            <div className="text-sm text-gray-800">{doctor.celular}</div>
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Email:</label>
                                            <div className="text-sm text-gray-800">{doctor.email}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Página Web:</label>
                                            <div className="text-sm text-gray-800">{doctor.pagina}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">RFC:</label>
                                            <div className="text-sm text-gray-800">{doctor.rfc}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Visitado:</label>
                                            <div className="text-sm text-gray-800">{doctor.visitar === 1 ? "Sí" : "No"}</div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Especialidades:</label>
                                            {doctor.specialties?.map(item => (
                                                <div key={item.id} className="text-sm text-gray-800">{item.especialidad}</div>
                                            ))}
                                        </div>

                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Instituciones:</label>
                                            {doctor.institutions?.map(item => (
                                                <div key={item.id} className="text-sm text-gray-800">{item.institucion}</div>
                                            ))}
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="text-xs font-medium text-gray-500">Consultorios:</label>
                                            {doctor.doctor_offices?.map(item => (
                                                <div key={item.id} className="text-sm text-gray-800 p-2 mt-1 bg-gray-50 rounded border border-gray-100">
                                                    <div className="font-medium">{item.nombre}</div>
                                                    <div className="text-gray-600">Horario: {item.horario}</div>
                                                    <div className="text-gray-600">Tels: {item.telefonos}</div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="md:col-span-4 border-t border-gray-100 pt-2 mt-2">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500">Observaciones Dental:</label>
                                                    <div className="text-sm text-gray-800">{doctor.observaciones_dental}</div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500">Observaciones Generales:</label>
                                                    <div className="text-sm text-gray-800">{doctor.observaciones_generales}</div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500">Observaciones Importantes:</label>
                                                    <div className="text-sm text-gray-800">{doctor.observaciones_importantes}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    </>
    );
};

export default Doctores;
